import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { css, keyframes, Theme } from '@emotion/react';
import { transparentize } from 'polished';
import { Check, CheckCircle, Circle, RocketLaunch, CircleDashed } from '@phosphor-icons/react';
import { useAccount } from 'client/lib/User';
import { Banner } from 'client/components/Banner';
import { Button } from 'client/components/Button';
import { Dialog } from 'client/components/Dialog/Dialog';
import { Help } from 'client/components/Help';
import { EllipsisLoader } from 'client/components/Other/EllipsisLoader';
import { Typography } from 'client/components/Typography';
import backgroundBlurImage from 'client/static/image/blur-background-1.png';

// FIXME: Actually add these steps...
const actualSteps = [
  'template',
  // 'project',
  // 'deployment',
  'done',
] as const;

const steps = [
  {
    name: 'Configuring Template',
    delayInSeconds: 3,
    // waitForStep: 'template' as const,
  },
  {
    name: 'Creating Project',
    delayInSeconds: 4,
    // waitForStep: 'project' as const,
  },
  {
    name: 'Creating Deployment',
    delayInSeconds: 4,
  },
  {
    name: 'Starting Deployment',
    delayInSeconds: 3,
  },
  {
    name: 'View Deployment',
    // waitForStep: 'done' as const,
    delayInSeconds: 0,
    done: true,
  },
];

export interface QuickLaunchDialogProps {
  data: {
    projectID: number;
    deploymentID: number;
    accountIdentifier: string;
    isCoreConnected: boolean;
    isReady: boolean;
    isStorageReady: boolean;
    isContainersReady: boolean;
  } | null;
  error?: string | null;
  hideBackdrop?: boolean;
}

export const QuickLaunchDialog = ({ data, error, hideBackdrop }: QuickLaunchDialogProps) => {
  const navigate = useNavigate();
  const stepIndexRef = useRef<number>(0);
  // const actualStepRef = useRef<(typeof actualSteps)[number]>('template');
  const [step, setStep] = useState<(typeof steps)[number]>(steps[0]);

  const redirectUrl = data ? `/${data.accountIdentifier}/project/${data.projectID}/deployment/${data.deploymentID}` : `/`;

  const handleRedirect = () => {
    navigate(redirectUrl, { replace: true });
  };

  useEffect(() => {
    if (!data) return;

    let seconds = 0;
    const interval = setInterval(() => {
      seconds++;
      const step = steps[stepIndexRef.current];
      if (step.delayInSeconds <= seconds) {
        // if (step.waitForStep && actualSteps.indexOf(actualStepRef.current) <= actualSteps.indexOf(step.waitForStep)) return;
        if (stepIndexRef.current >= steps.length - 1) return;
        stepIndexRef.current++;
        setStep(steps[stepIndexRef.current]);

        seconds = 0;
      }
      // Redirect if past last step or deployment is ready
      // NOTE! Experimenting here with moving forward if storage is ready
      if (stepIndexRef.current == steps.length - 1 || data?.isReady /*|| data?.isContainersReady*/) {
        setTimeout(() => {
          handleRedirect();
        }, 2000);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [data]);

  return (
    <Dialog
      open
      title="Quick Launching Project"
      description="This won't take long, sit tight"
      icon={<RocketLaunch style={{ fill: 'url(#gradient-two)' }} />}
      dismissable={false}
      hideBackdrop={hideBackdrop}
      actions={null}
      css={dialogStyle}
    >
      <div css={tipsStyle}>
        {/* Loader */}
        {!error && (
          <Typography component="ul" css={listStyle}>
            {steps.slice(0, -1).map((step, index) => (
              <li key={index}>
                {stepIndexRef.current > index && <CheckCircle weight="fill" />}
                {stepIndexRef.current == index && <Circle weight="fill" />}
                {stepIndexRef.current < index && <Circle />}
                {step.name} {stepIndexRef.current == index && <EllipsisLoader />}
              </li>
            ))}
          </Typography>
        )}

        {/* Error */}
        {!!error && <Banner type="error">Failed to start a trial ({error}). Please try again later…</Banner>}
      </div>

      <Help>
        Quick Launch let's you to quickly try things out with no configuration. Please note that no GitHub repository is created so you
        won't be a able to commit your changes.
      </Help>

      <Button
        disabled={!step.done}
        primary
        large
        style={{ '--progress': `${(stepIndexRef.current / (steps.length - 2)) * 100}%` } as CSSProperties}
        css={buttonStyle}
        onClick={handleRedirect}
      >
        View Deployment
      </Button>
    </Dialog>
  );
};

const dialogStyle = css`
  .MuiModal-backdrop {
    background-color: rgba(0, 0, 0, 0.8);
  }

  > .MuiDialog-container > .MuiPaper-root {
    padding: 0;
    height: calc(100vh - 2rem);
    max-height: 425px;

    > .MuiListItem-root {
      padding: 24px 32px 0 32px;
    }

    > .MuiDialogContent-root {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 20px 32px 28px 32px;
      overflow: hidden;

      > a {
        width: 100%;
      }
    }
  }
`;

const tipsStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  flex: 1;
`;

const listStyle = css`
  padding: 0;
  margin: 0;
  width: 250px;
  list-style: none;

  > li {
    display: flex;
    align-items: center;
    gap: 5px;
  }
`;

const buttonStyle = (theme: Theme) => css`
  --progress: 0%;

  position: relative;
  align-self: stretch;
  width: 100%;
  background-color: ${transparentize(0.5, theme.color.grey[60])};
  overflow: hidden;

  &.disabled > span {
    mix-blend-mode: overlay;
    color: rgba(255, 255, 255, 0.8);
  }

  > * {
    z-index: 2;
  }

  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: var(--progress);
    background-color: ${theme.color.primary[100]};
    z-index: 1;

    transition: width ${6000}ms ease-in-out;
  }
`;
